import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AuthPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const [mode, setMode] = useState(refFromUrl ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState(refFromUrl);
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [isAdult, setIsAdult] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (refFromUrl) setReferralCode(refFromUrl);
  }, [refFromUrl]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password, code || null);
      } else {
        await register(email, password, name, referralCode || null, agreed, isAdult);
      }
      toast.success("Welcome.");
      nav("/app");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailCode = typeof detail === "object" ? detail.code : null;
      if (detailCode === "totp_required") {
        setNeedsCode(true);
        toast.message("Enter the 6-digit code from your authenticator.");
      } else if (detailCode === "totp_invalid") {
        toast.error("That code doesn’t match. Try again.");
        setCode("");
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="auth-page">
      <div className="hidden md:flex flex-col justify-between p-12 bg-canvas-warm border-r border-line">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-logo">
          <div className="w-7 h-7 rounded-full bg-clay/90 grid place-items-center">
            <Sparkle weight="fill" className="text-canvas" size={14} />
          </div>
          <span className="font-serif-display text-2xl text-ink">Clarity Mirror</span>
        </Link>
        <motion.blockquote
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="max-w-md"
        >
          <p className="font-serif-display text-3xl leading-snug text-ink italic">
            ”Between stimulus and response there is a space. In that space is our power to choose.”
          </p>
          <footer className="mt-6 text-sm text-ink-mute uppercase tracking-[0.25em]">Viktor Frankl</footer>
        </motion.blockquote>
        <p className="text-sm text-ink-mute">Private by default. Your words remain yours.</p>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16 bg-canvas">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <h1 className="font-serif-display text-4xl md:text-5xl mb-3 text-ink">
            {mode === "login" ? "Welcome back." : "Begin softly."}
          </h1>
          <p className="text-ink-soft mb-10">
            {mode === "login" ? "Continue where you left off." : "A quiet account. 14 days free, no card needed."}
          </p>

          <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
            {mode === "register" && (
              <Field
                label="Your name"
                value={name}
                onChange={setName}
                placeholder="What should we call you?"
                testid="auth-name"
                required
              />
            )}
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="you@example.com"
              testid="auth-email"
              required
            />
            <Field
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="At least 6 characters"
              testid="auth-password"
              required
            />
            {mode === "register" && refFromUrl && (
              <Field
                label="Friend's invite code"
                value={referralCode}
                onChange={setReferralCode}
                placeholder=""
                testid="auth-referral"
              />
            )}
            {mode === "login" && needsCode && (
              <Field
                label="Two-factor code"
                value={code}
                onChange={setCode}
                placeholder="123 456"
                testid="auth-totp"
                required
              />
            )}
            {mode === "register" && (
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isAdult}
                    onChange={(e) => setIsAdult(e.target.checked)}
                    data-testid="auth-confirm-adult"
                    className="mt-0.5 h-4 w-4 rounded border-line-strong accent-[#B0745B] cursor-pointer"
                  />
                  <span className="text-sm text-ink-soft leading-relaxed">
                    I confirm that I am <b>18 years of age or older</b>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    data-testid="auth-accept-terms"
                    className="mt-0.5 h-4 w-4 rounded border-line-strong accent-[#B0745B] cursor-pointer"
                  />
                  <span className="text-sm text-ink-soft leading-relaxed">
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" data-testid="auth-terms-link" className="text-clay hover:text-clay-dark underline underline-offset-4">
                      user agreement &amp; privacy terms
                    </Link>{" "}
                    and the{" "}
                    <Link to="/terms-of-service" target="_blank" data-testid="auth-tos-link" className="text-clay hover:text-clay-dark underline underline-offset-4">
                      Terms of Service
                    </Link>
                  </span>
                </label>
              </div>
            )}
            <button
              type="submit"
              disabled={loading || (mode === "register" && (!agreed || !isAdult))}
              data-testid="auth-submit"
              className="group inline-flex items-center gap-2 rounded-full bg-ink text-canvas px-8 py-3.5 text-sm font-medium hover:bg-ink/90 transition-all active:scale-95 disabled:opacity-50"
            >
              {loading ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight weight="bold" size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>

          {mode === "register" && (
            <p className="mt-6 text-xs text-ink-mute">
              Your consent is recorded on your account when you join.
            </p>
          )}

          <p className="mt-10 text-sm text-ink-soft">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              type="button"
              data-testid="auth-toggle"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-clay hover:text-clay-dark underline underline-offset-4"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, required }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ink-mute">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-0 border-b border-line py-3 text-ink placeholder:text-ink-mute focus:border-clay focus:outline-none transition-colors text-base"
      />
    </label>
  );
}
